import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import CreditBlockCard from '../components/CreditBlockCard';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Upload, Loader2, Mic, FileText, Music, Layers, CheckCircle2,
  AlertCircle, Phone, Search, Download, Plus, Trash2, X,
} from 'lucide-react';
import { toast } from 'sonner';

/* ─────────────────────────────────────────────────────────────
   Source-type badge colours / labels
───────────────────────────────────────────────────────────── */
const SOURCE_META = {
  audio:    { label: 'Audio Only',      color: 'bg-blue-100 text-blue-700',   icon: Music },
  log:      { label: 'Log Only',        color: 'bg-purple-100 text-purple-700', icon: FileText },
  combined: { label: 'Audio + Log',     color: 'bg-emerald-100 text-emerald-700', icon: Layers },
};

function SourceBadge({ sourceType }) {
  const meta = SOURCE_META[sourceType] || SOURCE_META.audio;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${meta.color}`}>
      <Icon size={10} />
      {meta.label}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────
   Upload-type toggle
───────────────────────────────────────────────────────────── */
const UPLOAD_TYPES = [
  { id: 'audio',    label: 'Audio Only',   icon: Music,    desc: 'MP3 or WAV recording' },
  { id: 'log',      label: 'Log Only',     icon: FileText, desc: 'TXT, CSV or PDF log' },
  { id: 'combined', label: 'Audio + Log',  icon: Layers,   desc: 'Recording + log file' },
];

const CallUpload = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [sops, setSops] = useState([]);
  const [calls, setCalls] = useState([]);
  const [agents, setAgents] = useState([]);

  const [selectedSOP, setSelectedSOP] = useState('');
  const [selectedCall, setSelectedCall] = useState(location.state?.callId || '');
  const [isUploading, setIsUploading] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isAssessing, setIsAssessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [creditBlock, setCreditBlock] = useState(null); // { action, balance, required }

  // Upload type: 'audio' | 'log' | 'combined'
  const [uploadType, setUploadType] = useState('audio');

  const [uploadForm, setUploadForm] = useState({
    title: '',
    agent_id: '',
    audioFile: null,
    logFile: null,
  });

  // ── Multi-upload state ──────────────────────────────────────
  const [isMultiMode, setIsMultiMode] = useState(false);
  // queue item: { id, audioFile, logFile, status: 'pending'|'uploading'|'done'|'error', error }
  const [multiQueue, setMultiQueue] = useState([]);
  const [multiTitle, setMultiTitle] = useState('');
  const [multiAgentId, setMultiAgentId] = useState('');
  const [multiCustomer, setMultiCustomer] = useState(''); // customer ID / phone / name
  const [isUploadingAll, setIsUploadingAll] = useState(false);
  const audioMultiRef = useRef(null);
  const logMultiRef   = useRef(null);

  // Telephony state
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [telSearch, setTelSearch] = useState({ phone: '', agent: '', start_date: '', end_date: '' });
  const [isFetching, setIsFetching] = useState(false);
  const [fetchedCalls, setFetchedCalls] = useState([]);
  const [importingId, setImportingId] = useState(null);

  useEffect(() => {
    fetchSOPs();
    fetchCalls();
    fetchAgents();
    fetchProviders();
  }, []);

  // When a call is selected, populate transcript if already transcribed
  useEffect(() => {
    if (!selectedCall) return;
    const call = calls.find((c) => c.id === selectedCall);
    setTranscript(call?.transcript || '');
    setCreditBlock(null);
  }, [selectedCall, calls]);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents?status=active`);
      setAgents(response.data);
    } catch (err) {
      console.error('Failed to fetch agents:', err);
    }
  };

  const fetchSOPs = async () => {
    try {
      const response = await axios.get(`${API}/sops`);
      setSops(response.data);
    } catch (err) {
      console.error('Failed to fetch SOPs:', err);
    }
  };

  const fetchCalls = async () => {
    try {
      const response = await axios.get(`${API}/calls`);
      setCalls(response.data);
    } catch (err) {
      console.error('Failed to fetch calls:', err);
    }
  };

  const fetchProviders = async () => {
    try {
      const res = await axios.get(`${API}/integrations/telephony`);
      setProviders(res.data.filter((p) => p.is_active));
    } catch (err) {
      console.error('Failed to fetch providers:', err);
    }
  };

  const handleFetchFromProvider = async () => {
    if (!selectedProvider) { toast.error('Select a telephony provider first'); return; }
    if (!telSearch.phone && !telSearch.agent) {
      toast.error('Enter a phone number or agent identifier to search');
      return;
    }
    setIsFetching(true);
    setFetchedCalls([]);
    try {
      const params = {};
      if (telSearch.phone)      params.phone_number = telSearch.phone;
      if (telSearch.agent)      params.agent_search = telSearch.agent;
      if (telSearch.start_date) params.start_date   = telSearch.start_date;
      if (telSearch.end_date)   params.end_date     = telSearch.end_date;
      const res = await axios.get(`${API}/integrations/telephony/${selectedProvider}/fetch-calls`, { params });
      setFetchedCalls(res.data.calls || []);
      toast.success(`Found ${res.data.count} call(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch calls from provider');
    } finally {
      setIsFetching(false);
    }
  };

  const handleImportCall = async (fc) => {
    setImportingId(fc.external_call_id);
    try {
      const fd = new FormData();
      fd.append('external_call_id', fc.external_call_id);
      fd.append('caller_number', fc.caller_number);
      fd.append('recording_url', fc.recording_url);
      fd.append('title', `Call ${fc.caller_number} (${fc.external_call_id.slice(0, 8)})`);
      const res = await axios.post(
        `${API}/integrations/telephony/${fc.provider_id}/import-call`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Call imported successfully!');
      setSelectedCall(res.data.id);
      fetchCalls();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImportingId(null);
    }
  };

  /* ── Multi-queue helpers ─────────────────────────────────── */
  const newQueueItem = (audioFile = null, logFile = null) => ({
    id: `qi-${Date.now()}-${Math.random()}`,
    audioFile,
    logFile,
    status: 'pending',
    error: '',
  });

  // Add files from multi-select input (audio or log)
  const addFilesToQueue = (files, field) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    if (uploadType === 'combined') {
      // For combined mode each slot is paired per row — handled separately
      return;
    }
    setMultiQueue((prev) => [
      ...prev,
      ...arr.map((f) => newQueueItem(
        field === 'audio' ? f : null,
        field === 'log'   ? f : null,
      )),
    ]);
  };

  // For combined mode: add an empty row
  const addCombinedRow = () =>
    setMultiQueue((prev) => [...prev, newQueueItem()]);

  // Update a combined row's audio or log file
  const updateCombinedRow = (id, field, file) =>
    setMultiQueue((prev) =>
      prev.map((item) => item.id === id ? { ...item, [field === 'audio' ? 'audioFile' : 'logFile']: file } : item)
    );

  const removeQueueItem = (id) =>
    setMultiQueue((prev) => prev.filter((item) => item.id !== id));

  // Upload all queued items sequentially
  const handleUploadAll = async () => {
    if (!multiTitle.trim()) { toast.error('Enter a base title for the calls'); return; }
    const pending = multiQueue.filter((q) => q.status === 'pending');
    if (pending.length === 0) { toast.error('No pending calls in the queue'); return; }

    setIsUploadingAll(true);
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < multiQueue.length; i++) {
      const item = multiQueue[i];
      if (item.status !== 'pending') continue;

      // Validate
      const needsAudio = uploadType === 'audio' || uploadType === 'combined';
      const needsLog   = uploadType === 'log'   || uploadType === 'combined';
      if (needsAudio && !item.audioFile) {
        setMultiQueue((prev) => prev.map((q) => q.id === item.id
          ? { ...q, status: 'error', error: 'Missing audio file' } : q));
        failCount++;
        continue;
      }
      if (needsLog && !item.logFile) {
        setMultiQueue((prev) => prev.map((q) => q.id === item.id
          ? { ...q, status: 'error', error: 'Missing log file' } : q));
        failCount++;
        continue;
      }

      // Mark uploading
      setMultiQueue((prev) => prev.map((q) => q.id === item.id ? { ...q, status: 'uploading' } : q));

      // ── Title generation ─────────────────────────────────────
      // For log-only: prefer filename as the ticket/call identifier
      // For audio / combined: use numeric index
      let callTitle;
      const customerPart = multiCustomer.trim() ? ` — ${multiCustomer.trim()}` : '';
      if (uploadType === 'log' && item.logFile) {
        // Strip extension from filename for a clean title
        const baseName = item.logFile.name.replace(/\.[^.]+$/, '');
        callTitle = multiTitle.trim()
          ? `${multiTitle}${customerPart} — ${baseName}`
          : `${multiCustomer.trim() || 'Log'} — ${baseName}`;
      } else {
        const pendingTotal = multiQueue.length;
        callTitle = multiQueue.length === 1
          ? `${multiTitle}${customerPart}`
          : `${multiTitle}${customerPart} (${i + 1} of ${pendingTotal})`;
      }

      const fd = new FormData();
      fd.append('title', callTitle);
      if (multiAgentId) fd.append('agent_id', multiAgentId);
      if (item.audioFile) fd.append('file', item.audioFile);
      if (item.logFile)   fd.append('log_file', item.logFile);

      try {
        await axios.post(`${API}/calls/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setMultiQueue((prev) => prev.map((q) => q.id === item.id ? { ...q, status: 'done' } : q));
        successCount++;
      } catch (err) {
        const msg = err.response?.data?.detail || 'Upload failed';
        setMultiQueue((prev) => prev.map((q) => q.id === item.id
          ? { ...q, status: 'error', error: msg } : q));
        failCount++;
      }
    }

    setIsUploadingAll(false);
    fetchCalls();

    if (successCount > 0) toast.success(`${successCount} call(s) uploaded successfully!`);
    if (failCount > 0) toast.error(`${failCount} call(s) failed`);

    // Clear done items after success
    if (failCount === 0) {
      setMultiQueue([]);
      setMultiTitle('');
    }
  };

  const resetMultiQueue = () => {
    setMultiQueue([]);
    setMultiTitle('');
    setMultiAgentId('');
    setMultiCustomer('');
    if (audioMultiRef.current) audioMultiRef.current.value = '';
    if (logMultiRef.current)   logMultiRef.current.value   = '';
  };

  /* ── Upload handler ─────────────────────────────────────── */
  const handleUpload = async (e) => {
    e.preventDefault();

    const needsAudio = uploadType === 'audio' || uploadType === 'combined';
    const needsLog   = uploadType === 'log'   || uploadType === 'combined';

    if (needsAudio && !uploadForm.audioFile) {
      toast.error('Please select an audio file (MP3 / WAV)');
      return;
    }
    if (needsLog && !uploadForm.logFile) {
      toast.error('Please select a log file (TXT / CSV / PDF)');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('title', uploadForm.title);
      if (uploadForm.agent_id) formData.append('agent_id', uploadForm.agent_id);
      if (uploadForm.audioFile) formData.append('file', uploadForm.audioFile);
      if (uploadForm.logFile)   formData.append('log_file', uploadForm.logFile);

      const response = await axios.post(`${API}/calls/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Uploaded successfully!');
      setSelectedCall(response.data.id);
      setUploadForm({ title: '', agent_id: '', audioFile: null, logFile: null });
      fetchCalls();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  /* ── Transcribe / use-log-as-transcript handler ─────────── */
  const handleTranscribe = async () => {
    if (!selectedCall) {
      toast.error('Please select a call recording');
      return;
    }

    setCreditBlock(null);
    setIsTranscribing(true);
    try {
      const response = await axios.post(`${API}/calls/${selectedCall}/transcribe`);
      setTranscript(response.data.transcript);

      const sourceLabel = {
        log:      'Log text loaded as transcript!',
        combined: 'Audio transcribed & log appended!',
        audio:    'Transcription completed!',
      }[response.data.source] || 'Transcription completed!';

      toast.success(sourceLabel);
    } catch (error) {
      if (error.response?.status === 402) {
        const d = error.response.data?.detail || {};
        setCreditBlock({ action: 'AI transcription', balance: d.balance ?? 0, required: d.required });
      } else {
        toast.error(error.response?.data?.detail || 'Transcription failed');
      }
    } finally {
      setIsTranscribing(false);
    }
  };

  /* ── Assess handler ──────────────────────────────────────── */
  const handleAssess = async () => {
    if (!selectedCall) { toast.error('Please select a call'); return; }
    if (!selectedSOP)  { toast.error('Please select an SOP'); return; }
    if (!transcript)   { toast.error('Please transcribe / load the call first'); return; }

    setCreditBlock(null);
    setIsAssessing(true);
    try {
      const formData = new FormData();
      formData.append('sop_id', selectedSOP);
      await axios.post(`${API}/calls/${selectedCall}/assess`, formData);
      toast.success('Assessment completed!');
      navigate(`/assessment/${selectedCall}`);
    } catch (error) {
      if (error.response?.status === 402) {
        const d = error.response.data?.detail || {};
        setCreditBlock({ action: 'AI assessment', balance: d.balance ?? 0, required: d.required });
      } else {
        toast.error(error.response?.data?.detail || 'Assessment failed');
      }
    } finally {
      setIsAssessing(false);
    }
  };

  /* ── Helpers ─────────────────────────────────────────────── */
  const selectedCallObj = calls.find((c) => c.id === selectedCall);
  const callSourceType  = selectedCallObj?.source_type || 'audio';

  const transcribeLabel = () => {
    if (isTranscribing) return 'Processing…';
    if (callSourceType === 'log')      return 'Load Log as Transcript';
    if (callSourceType === 'combined') return 'Transcribe Audio & Append Log';
    return 'Transcribe Call';
  };

  /* ─────────────────────────────────────────────────────────
     RENDER
  ────────────────────────────────────────────────────────── */
  return (
    <Layout>
      <div className="space-y-8 fade-in" data-testid="call-upload-page">

        {/* Header */}
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Upload & Assess Calls
          </h1>
          <p className="text-slate-600">
            Upload call recordings, agent logs, or both — then assess against SOPs
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

          {/* ── LEFT: Upload ─────────────────────────────────────── */}
          <Card className="p-6 border border-slate-200 shadow-sm">
            {/* Card header with Single / Multiple toggle */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Upload New Call
              </h2>
              <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => { setIsMultiMode(false); resetMultiQueue(); }}
                  className={`px-3 py-1.5 transition-all ${!isMultiMode ? 'bg-[#4338CA] text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                  data-testid="single-mode-btn"
                >
                  Single
                </button>
                <button
                  type="button"
                  onClick={() => setIsMultiMode(true)}
                  className={`px-3 py-1.5 transition-all ${isMultiMode ? 'bg-[#4338CA] text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                  data-testid="multi-mode-btn"
                >
                  Multiple
                </button>
              </div>
            </div>

            {/* Upload-type toggle (shared for both modes) */}
            <div className="grid grid-cols-3 gap-2 mb-5">
              {UPLOAD_TYPES.map(({ id, label, icon: Icon, desc }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => { setUploadType(id); resetMultiQueue(); }}
                  className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 text-center transition-all
                    ${uploadType === id
                      ? 'border-[#4338CA] bg-indigo-50 text-[#4338CA]'
                      : 'border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                >
                  <Icon size={18} />
                  <span className="text-xs font-semibold leading-tight">{label}</span>
                  <span className="text-[10px] text-slate-400 leading-tight">{desc}</span>
                </button>
              ))}
            </div>

            {/* ── SINGLE MODE ─────────────────────────────────────── */}
            {!isMultiMode && (
              <form onSubmit={handleUpload} className="space-y-4" data-testid="upload-form">
                <div>
                  <Label htmlFor="call-title">Call Title</Label>
                  <Input id="call-title" placeholder="e.g., Customer Support Call #123"
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                    required data-testid="call-title-input" />
                </div>
                <div>
                  <Label htmlFor="agent-select">Agent (Optional)</Label>
                  <Select value={uploadForm.agent_id || 'none'}
                    onValueChange={(v) => setUploadForm({ ...uploadForm, agent_id: v === 'none' ? '' : v })}>
                    <SelectTrigger id="agent-select" data-testid="agent-select">
                      <SelectValue placeholder="Select agent" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Agent</SelectItem>
                      {agents.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.name} ({a.agent_id})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {(uploadType === 'audio' || uploadType === 'combined') && (
                  <div>
                    <Label htmlFor="audio-file" className="flex items-center gap-1.5">
                      <Music size={13} className="text-blue-500" />Audio File (MP3, WAV)
                    </Label>
                    <Input id="audio-file" type="file" accept=".mp3,.wav" className="mt-1"
                      onChange={(e) => setUploadForm({ ...uploadForm, audioFile: e.target.files[0] })}
                      required={uploadType === 'audio' || uploadType === 'combined'}
                      data-testid="audio-file-input" />
                    <p className="text-xs text-slate-400 mt-1">Max size: 25 MB</p>
                  </div>
                )}
                {(uploadType === 'log' || uploadType === 'combined') && (
                  <div>
                    <Label htmlFor="log-file" className="flex items-center gap-1.5">
                      <FileText size={13} className="text-purple-500" />Agent Log File (TXT, CSV, PDF)
                    </Label>
                    <Input id="log-file" type="file" accept=".txt,.csv,.pdf" className="mt-1"
                      onChange={(e) => setUploadForm({ ...uploadForm, logFile: e.target.files[0] })}
                      required={uploadType === 'log' || uploadType === 'combined'}
                      data-testid="log-file-input" />
                    <p className="text-xs text-slate-400 mt-1">TXT / CSV / PDF — text extracted automatically</p>
                  </div>
                )}
                <Button type="submit" className="w-full bg-[#4338CA] hover:bg-[#3730A3] text-white"
                  disabled={isUploading} data-testid="upload-button">
                  {isUploading
                    ? <><Loader2 className="mr-2 animate-spin" size={16} />Uploading…</>
                    : <><Upload className="mr-2" size={16} />Upload Call</>}
                </Button>
              </form>
            )}

            {/* ── MULTIPLE MODE ────────────────────────────────────── */}
            {isMultiMode && (
              <div className="space-y-4" data-testid="multi-upload-section">
                {/* Base title + customer + agent */}
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label htmlFor="multi-title">
                      {uploadType === 'log' ? 'Base Title / Category' : 'Base Call Title'}
                    </Label>
                    <Input id="multi-title"
                      placeholder={uploadType === 'log'
                        ? 'e.g., Customer Tickets — July'
                        : 'e.g., Customer Support Calls — July'}
                      value={multiTitle}
                      onChange={(e) => setMultiTitle(e.target.value)}
                      data-testid="multi-title-input" />
                    <p className="text-xs text-slate-400 mt-0.5">
                      {uploadType === 'log'
                        ? 'Each log\'s filename will be appended automatically — e.g., "Base Title — ticket_001.pdf"'
                        : 'Each call will be named "Base Title (1 of N)", "Base Title (2 of N)"…'}
                    </p>
                  </div>

                  {/* Customer / Reference field */}
                  <div>
                    <Label htmlFor="multi-customer" className="flex items-center gap-1.5">
                      Customer / Reference
                      <span className="text-xs font-normal text-slate-400">(optional)</span>
                    </Label>
                    <Input id="multi-customer"
                      placeholder={uploadType === 'log'
                        ? 'e.g., +91-9876543210 or CUST-001'
                        : 'e.g., Customer name or phone'}
                      value={multiCustomer}
                      onChange={(e) => setMultiCustomer(e.target.value)}
                      data-testid="multi-customer-input" />
                    <p className="text-xs text-slate-400 mt-0.5">
                      {uploadType === 'log'
                        ? 'Groups all tickets under the same customer for batch auditing'
                        : 'Added to each call title for easy identification'}
                    </p>
                  </div>

                  <div>
                    <Label>Agent (Optional — applied to all)</Label>
                    <Select value={multiAgentId || 'none'}
                      onValueChange={(v) => setMultiAgentId(v === 'none' ? '' : v)}>
                      <SelectTrigger data-testid="multi-agent-select">
                        <SelectValue placeholder="Select agent" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Agent</SelectItem>
                        {agents.map((a) => (
                          <SelectItem key={a.id} value={a.id}>{a.name} ({a.agent_id})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* File picker for audio/log modes */}
                {uploadType !== 'combined' && (
                  <div>
                    <Label className="flex items-center gap-1.5">
                      {uploadType === 'audio'
                        ? <><Music size={13} className="text-blue-500" />Select Audio Files (MP3, WAV)</>
                        : <><FileText size={13} className="text-purple-500" />Select Log / Ticket Files (TXT, CSV, PDF)</>}
                    </Label>
                    <Input
                      ref={uploadType === 'audio' ? audioMultiRef : logMultiRef}
                      type="file"
                      multiple
                      accept={uploadType === 'audio' ? '.mp3,.wav' : '.txt,.csv,.pdf'}
                      className="mt-1"
                      data-testid="multi-file-input"
                      onChange={(e) => addFilesToQueue(e.target.files, uploadType === 'audio' ? 'audio' : 'log')}
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      {uploadType === 'log'
                        ? 'Select multiple ticket/log files — each becomes a separate auditable call record'
                        : 'Select multiple files at once — each becomes a separate call'}
                    </p>
                  </div>
                )}

                {/* Combined mode: row-based pairs */}
                {uploadType === 'combined' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Audio + Log Pairs</Label>
                      <Button type="button" size="sm" variant="outline"
                        onClick={addCombinedRow} className="h-7 text-xs"
                        data-testid="add-pair-button">
                        <Plus size={12} className="mr-1" />Add Row
                      </Button>
                    </div>
                    {multiQueue.length === 0 && (
                      <div className="p-3 text-center text-slate-400 text-sm border border-dashed border-slate-200 rounded-lg">
                        Click "Add Row" to add audio + log pairs
                      </div>
                    )}
                    {multiQueue.map((item, idx) => (
                      <div key={item.id}
                        className="flex items-start gap-2 p-3 border border-slate-200 rounded-lg bg-slate-50"
                        data-testid={`pair-row-${idx}`}>
                        <span className="text-xs font-semibold text-slate-500 mt-2 w-5 shrink-0">{idx + 1}</span>
                        <div className="flex-1 grid grid-cols-2 gap-2">
                          <div>
                            <p className="text-xs text-slate-500 mb-0.5 flex items-center gap-1">
                              <Music size={10} className="text-blue-500" />Audio
                            </p>
                            <Input type="file" accept=".mp3,.wav" className="h-7 text-xs p-1"
                              onChange={(e) => updateCombinedRow(item.id, 'audio', e.target.files[0])}
                              data-testid={`pair-audio-${idx}`} />
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-0.5 flex items-center gap-1">
                              <FileText size={10} className="text-purple-500" />Log
                            </p>
                            <Input type="file" accept=".txt,.csv,.pdf" className="h-7 text-xs p-1"
                              onChange={(e) => updateCombinedRow(item.id, 'log', e.target.files[0])}
                              data-testid={`pair-log-${idx}`} />
                          </div>
                        </div>
                        {/* Status badge */}
                        <div className="shrink-0 mt-1.5">
                          {item.status === 'done' && <CheckCircle2 size={16} className="text-emerald-500" />}
                          {item.status === 'uploading' && <Loader2 size={16} className="animate-spin text-indigo-500" />}
                          {item.status === 'error' && (
                            <span title={item.error}><AlertCircle size={16} className="text-rose-500" /></span>
                          )}
                          {item.status === 'pending' && (
                            <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0"
                              onClick={() => removeQueueItem(item.id)}>
                              <X size={14} className="text-slate-400" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {uploadType !== 'combined' && multiQueue.length > 0 && (
                  <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-slate-600">
                        {multiQueue.length} {uploadType === 'log' ? 'ticket/log' : 'file'}(s) queued
                      </p>
                      <Button type="button" variant="ghost" size="sm" className="h-6 text-xs text-slate-400"
                        onClick={resetMultiQueue}>Clear all</Button>
                    </div>
                    {multiQueue.map((item, idx) => {
                      const file = item.audioFile || item.logFile;
                      const isLog = !!item.logFile;
                      // For logs: strip extension for display as ticket name
                      const displayName = isLog
                        ? item.logFile.name.replace(/\.[^.]+$/, '')
                        : file?.name;
                      const fileExt = file?.name.split('.').pop().toUpperCase();
                      return (
                        <div key={item.id}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs
                            ${item.status === 'done'      ? 'bg-emerald-50 border-emerald-200'
                            : item.status === 'error'     ? 'bg-rose-50 border-rose-200'
                            : item.status === 'uploading' ? 'bg-indigo-50 border-indigo-200'
                            :                               'bg-slate-50 border-slate-200'}`}
                          data-testid={`queue-item-${idx}`}>
                          {item.status === 'done'      && <CheckCircle2 size={13} className="text-emerald-500 shrink-0" />}
                          {item.status === 'uploading' && <Loader2 size={13} className="animate-spin text-indigo-500 shrink-0" />}
                          {item.status === 'error'     && <AlertCircle size={13} className="text-rose-500 shrink-0" />}
                          {item.status === 'pending'   && (
                            isLog
                              ? <FileText size={13} className="text-purple-500 shrink-0" />
                              : <Music size={13} className="text-blue-500 shrink-0" />
                          )}
                          <span className="flex-1 truncate text-slate-700 font-medium">{displayName}</span>
                          {/* File ext badge */}
                          {fileExt && item.status === 'pending' && (
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0
                              ${isLog ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
                              {fileExt}
                            </span>
                          )}
                          <span className="text-slate-400 shrink-0 ml-1">
                            {item.status === 'done'      ? 'Uploaded'
                            : item.status === 'uploading' ? 'Uploading…'
                            : item.status === 'error'     ? item.error
                            : multiCustomer              ? multiCustomer.length > 12 ? multiCustomer.slice(0, 12) + '…' : multiCustomer
                            : ''}
                          </span>
                          {item.status === 'pending' && (
                            <Button type="button" variant="ghost" size="sm" className="h-5 w-5 p-0"
                              onClick={() => removeQueueItem(item.id)}>
                              <X size={12} className="text-slate-400" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Upload All button */}
                <div className="flex gap-2 pt-1">
                  <Button
                    type="button"
                    onClick={handleUploadAll}
                    disabled={isUploadingAll || multiQueue.length === 0}
                    className="flex-1 bg-[#4338CA] hover:bg-[#3730A3] text-white"
                    data-testid="upload-all-button"
                  >
                    {isUploadingAll
                      ? <><Loader2 className="mr-2 animate-spin" size={16} />Uploading…</>
                      : <>
                          <Upload className="mr-2" size={16} />
                          Upload All ({multiQueue.filter((q) => q.status === 'pending').length} pending)
                          {multiCustomer && <span className="ml-1 text-indigo-200 text-xs">— {multiCustomer}</span>}
                        </>}
                  </Button>
                  {multiQueue.some((q) => q.status === 'done') && (
                    <Button type="button" variant="outline" onClick={resetMultiQueue} className="shrink-0">
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            )}
          </Card>

          {/* ── RIGHT: Process ───────────────────────────────────── */}
          <Card className="p-6 border border-slate-200 shadow-sm">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Process Existing Call
            </h2>
            <div className="space-y-4">

              {/* Select call */}
              <div>
                <Label htmlFor="select-call">Select Call</Label>
                <Select value={selectedCall} onValueChange={setSelectedCall}>
                  <SelectTrigger id="select-call" data-testid="select-call-trigger">
                    <SelectValue placeholder="Choose a call recording" />
                  </SelectTrigger>
                  <SelectContent>
                    {calls.map((call) => (
                      <SelectItem key={call.id} value={call.id} data-testid={`call-option-${call.id}`}>
                        <span className="flex items-center gap-2">
                          {call.title}
                          <SourceBadge sourceType={call.source_type || 'audio'} />
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Selected call info */}
              {selectedCallObj && (
                <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 space-y-1.5 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-700">{selectedCallObj.title}</span>
                    <SourceBadge sourceType={selectedCallObj.source_type || 'audio'} />
                  </div>
                  {selectedCallObj.log_file_name && (
                    <div className="flex items-center gap-1.5 text-xs text-purple-600">
                      <FileText size={11} />
                      Log: {selectedCallObj.log_file_name}
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    {selectedCallObj.status === 'assessed' ? (
                      <><CheckCircle2 size={11} className="text-emerald-500" /> Already assessed</>
                    ) : selectedCallObj.status === 'transcribed' ? (
                      <><CheckCircle2 size={11} className="text-blue-500" /> Transcribed — ready to assess</>
                    ) : (
                      <><AlertCircle size={11} className="text-amber-500" /> Pending — needs transcription</>
                    )}
                  </div>
                </div>
              )}

              {/* SOP */}
              <div>
                <Label htmlFor="select-sop">Select SOP</Label>
                <Select value={selectedSOP} onValueChange={setSelectedSOP}>
                  <SelectTrigger id="select-sop" data-testid="select-sop-trigger">
                    <SelectValue placeholder="Choose an SOP" />
                  </SelectTrigger>
                  <SelectContent>
                    {sops.map((sop) => (
                      <SelectItem key={sop.id} value={sop.id} data-testid={`sop-option-${sop.id}`}>
                        {sop.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                {/* Credit block card (402 from transcribe / assess) */}
                {creditBlock && (
                  <CreditBlockCard
                    balance={creditBlock.balance}
                    required={creditBlock.required}
                    action={creditBlock.action}
                    onDismiss={() => setCreditBlock(null)}
                  />
                )}

                {/* Transcribe / Load Log button */}
                <Button
                  onClick={handleTranscribe}
                  variant="outline"
                  className="w-full border-slate-300"
                  disabled={!selectedCall || isTranscribing}
                  data-testid="transcribe-button"
                >
                  {isTranscribing ? (
                    <><Loader2 className="mr-2 animate-spin" size={16} />Processing…</>
                  ) : callSourceType === 'log' ? (
                    <><FileText className="mr-2" size={16} />{transcribeLabel()}</>
                  ) : callSourceType === 'combined' ? (
                    <><Layers className="mr-2" size={16} />{transcribeLabel()}</>
                  ) : (
                    <><Mic className="mr-2" size={16} />{transcribeLabel()}</>
                  )}
                </Button>

                {/* Transcript preview */}
                {transcript && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="transcript-preview">
                    <p className="text-xs text-emerald-700 font-medium mb-1">Transcript Preview</p>
                    <p className="text-sm text-slate-700 line-clamp-4 whitespace-pre-line">{transcript}</p>
                  </div>
                )}

                {/* Assess button */}
                <Button
                  onClick={handleAssess}
                  className="w-full bg-[#4338CA] hover:bg-[#3730A3] text-white"
                  disabled={!selectedCall || !selectedSOP || !transcript || isAssessing}
                  data-testid="assess-button"
                >
                  {isAssessing ? (
                    <><Loader2 className="mr-2 animate-spin" size={16} />Assessing…</>
                  ) : (
                    'Assess Against SOP'
                  )}
                </Button>
              </div>
            </div>
          </Card>

        </div>

        {/* ── Uploaded calls table ───────────────────────────────── */}
        {calls.length > 0 && (
          <Card className="p-6 border border-slate-200 shadow-sm">
            <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Uploaded Calls
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-2 pr-4 font-semibold text-slate-600">Title</th>
                    <th className="text-left py-2 pr-4 font-semibold text-slate-600">Type</th>
                    <th className="text-left py-2 pr-4 font-semibold text-slate-600">Log File</th>
                    <th className="text-left py-2 pr-4 font-semibold text-slate-600">Status</th>
                    <th className="text-left py-2 font-semibold text-slate-600">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((call) => (
                    <tr key={call.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="py-2 pr-4 font-medium text-slate-800">{call.title}</td>
                      <td className="py-2 pr-4">
                        <SourceBadge sourceType={call.source_type || 'audio'} />
                      </td>
                      <td className="py-2 pr-4 text-xs text-slate-500">
                        {call.log_file_name ? (
                          <span className="flex items-center gap-1 text-purple-600">
                            <FileText size={10} />{call.log_file_name}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="py-2 pr-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
                          ${call.status === 'assessed'    ? 'bg-emerald-100 text-emerald-700' :
                            call.status === 'transcribed' ? 'bg-blue-100 text-blue-700' :
                                                            'bg-amber-100 text-amber-700'}`}>
                          {call.status === 'assessed' ? <CheckCircle2 size={10} /> : <AlertCircle size={10} />}
                          {call.status}
                        </span>
                      </td>
                      <td className="py-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-7 border-slate-200"
                          onClick={() => setSelectedCall(call.id)}
                        >
                          Select
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* ── Fetch from Telephony Provider ─────────────────────── */}
        <Card className="p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Phone size={20} className="text-indigo-600" />
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Fetch Calls from Telephony Provider
            </h2>
          </div>

          {providers.length === 0 ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              No active telephony providers configured. Go to <strong>Integrations</strong> to add Knowlarity or Ozonetel.
            </div>
          ) : (
            <div className="space-y-4">
              {/* Provider select */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Provider</Label>
                  <Select value={selectedProvider || 'none'}
                    onValueChange={(v) => setSelectedProvider(v === 'none' ? '' : v)}>
                    <SelectTrigger data-testid="provider-select">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select provider</SelectItem>
                      {providers.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.display_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Search fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="tel-phone" className="flex items-center gap-1">
                    <Phone size={11} />Phone Number
                  </Label>
                  <Input id="tel-phone" placeholder="+91XXXXXXXXXX"
                    value={telSearch.phone}
                    onChange={(e) => setTelSearch({ ...telSearch, phone: e.target.value })}
                    data-testid="phone-number-input" />
                </div>
                <div>
                  <Label htmlFor="tel-agent">Agent Number / ID</Label>
                  <Input id="tel-agent" placeholder="Agent number or ID"
                    value={telSearch.agent}
                    onChange={(e) => setTelSearch({ ...telSearch, agent: e.target.value })}
                    data-testid="agent-search-input" />
                </div>
                <div>
                  <Label htmlFor="tel-from">From Date</Label>
                  <Input id="tel-from" type="date" value={telSearch.start_date}
                    onChange={(e) => setTelSearch({ ...telSearch, start_date: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="tel-to">To Date</Label>
                  <Input id="tel-to" type="date" value={telSearch.end_date}
                    onChange={(e) => setTelSearch({ ...telSearch, end_date: e.target.value })} />
                </div>
              </div>

              <Button onClick={handleFetchFromProvider} disabled={isFetching || !selectedProvider}
                className="bg-[#4338CA] hover:bg-[#3730A3] text-white"
                data-testid="fetch-calls-button">
                {isFetching
                  ? <><Loader2 size={15} className="mr-2 animate-spin" />Fetching…</>
                  : <><Search size={15} className="mr-2" />Fetch Calls</>}
              </Button>

              {/* Results */}
              {fetchedCalls.length > 0 && (
                <div className="overflow-x-auto mt-2">
                  <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Call ID</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Caller</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Agent #</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Duration</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Date</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fetchedCalls.map((fc) => (
                        <tr key={fc.external_call_id}
                          className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-2 text-xs font-mono text-slate-600">{fc.external_call_id.slice(0, 12)}…</td>
                          <td className="px-3 py-2 text-slate-700">{fc.caller_number}</td>
                          <td className="px-3 py-2 text-slate-700">{fc.agent_number || '—'}</td>
                          <td className="px-3 py-2 text-slate-600">{fc.duration ? `${fc.duration}s` : '—'}</td>
                          <td className="px-3 py-2 text-xs text-slate-500">
                            {fc.start_time ? new Date(fc.start_time).toLocaleDateString() : '—'}
                          </td>
                          <td className="px-3 py-2">
                            {fc.recording_url ? (
                              <Button size="sm" variant="outline" className="h-7 text-xs"
                                onClick={() => handleImportCall(fc)}
                                disabled={importingId === fc.external_call_id}
                                data-testid={`import-call-${fc.external_call_id}`}>
                                {importingId === fc.external_call_id
                                  ? <Loader2 size={12} className="animate-spin" />
                                  : <><Download size={12} className="mr-1" />Import</>}
                              </Button>
                            ) : (
                              <span className="text-xs text-slate-400">No recording</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {fetchedCalls.length === 0 && !isFetching && telSearch.phone || telSearch.agent
                ? null : null}
            </div>
          )}
        </Card>

      </div>
    </Layout>
  );
};

export default CallUpload;
